import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Divider, FormLabel } from "@mui/material";
import { extractErrorMessage } from "@platform/api";
import {
  Alert,
  Button,
  ConfirmationModal,
  Dialog,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@platform/design-system";
import { Controller, useForm, useWatch } from "react-hook-form";
import { z } from "zod";

import { BrokerChecklist, RepStatusChip } from "../components";
import { REP_TYPE_LABELS, REP_TYPES } from "../constants";
import {
  useCarrierRepsQuery,
  useDeactivateCarrierRepMutation,
  useSaveCarrierRepMutation,
} from "../hooks";
import { MOCK_BROKERS } from "../mock";

import type { ReactNode } from "react";
import type { CarrierRepRequest } from "../api/types";
import type { Carrier, CarrierRep, CarrierRepType } from "../types";

const REQUIRED_FIELD_MESSAGE = "Required field";
const DUPLICATE_REP_MESSAGE =
  "This carrier already has an active rep with this email and role.";

const carrierRepSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, REQUIRED_FIELD_MESSAGE)
    .email("Enter a valid email"),
  name: z.string().trim(),
  title: z.string().trim(),
  phone: z.string().trim(),
  type: z.enum(REP_TYPES),
  notes: z.string().trim(),
});

type CarrierRepFormValues = z.infer<typeof carrierRepSchema>;

const hasActiveRep = (
  reps: CarrierRep[],
  email: string,
  type: CarrierRepType,
): boolean =>
  reps.some((rep) => rep.active && rep.email === email && rep.type === type);

const DuplicateCheck = ({
  isDuplicate,
}: {
  isDuplicate: boolean;
}): React.JSX.Element =>
  isDuplicate ? (
    <Alert severity="error">{DUPLICATE_REP_MESSAGE}</Alert>
  ) : (
    <Alert severity="success">No duplicate or overlapping reps found.</Alert>
  );

const DIALOG_WIDTH = 780;
const ADD_REP_ERROR_MESSAGE = "Unable to add the rep.";
const UPDATE_REP_ERROR_MESSAGE = "Unable to update the rep.";
const DEACTIVATE_REP_ERROR_MESSAGE = "Unable to deactivate the rep.";

const emptyToNull = (value: string): string | null => value || null;

const toCarrierRepRequest = (
  values: CarrierRepFormValues,
): CarrierRepRequest => ({
  email: values.email,
  type: values.type,
  name: emptyToNull(values.name),
  title: emptyToNull(values.title),
  phone: emptyToNull(values.phone),
  notes: emptyToNull(values.notes),
});

const NEW_REP_VALUES: CarrierRepFormValues = {
  email: "",
  name: "",
  title: "",
  phone: "",
  type: "sales_rep",
  notes: "",
};

const toFormValues = (rep: CarrierRep): CarrierRepFormValues => ({
  email: rep.email,
  name: rep.name ?? "",
  title: rep.title ?? "",
  phone: rep.phone ?? "",
  type: rep.type,
  notes: rep.notes ?? "",
});

const FormField = ({
  label,
  htmlFor,
  labelId,
  children,
}: {
  label: string;
  htmlFor: string;
  labelId?: string;
  children: ReactNode;
}): React.JSX.Element => (
  <Box display="flex" flexDirection="column" gap={0.5}>
    <FormLabel htmlFor={htmlFor} id={labelId}>
      <Typography variant="caption" component="span" color="textSecondary">
        {label}
      </Typography>
    </FormLabel>
    {children}
  </Box>
);

const SectionTitle = ({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children?: ReactNode;
}): React.JSX.Element => (
  <Box display="flex" alignItems="baseline" gap={1}>
    <Typography variant="overline" component="h3" color="brandPrimary">
      {title}
    </Typography>
    {description && (
      <Typography variant="caption" component="span" color="textSecondary">
        {description}
      </Typography>
    )}
    {children}
  </Box>
);

interface CarrierRepDialogProps {
  carrier: Carrier;
  rep?: CarrierRep;
  onClose: () => void;
}

export const CarrierRepDialog = ({
  carrier,
  rep,
  onClose,
}: CarrierRepDialogProps): React.JSX.Element => {
  const { repList } = useCarrierRepsQuery(carrier.id);
  const { control, handleSubmit, clearErrors } = useForm<CarrierRepFormValues>({
    resolver: zodResolver(carrierRepSchema),
    defaultValues: rep ? toFormValues(rep) : NEW_REP_VALUES,
    reValidateMode: "onSubmit",
  });
  const [email, type] = useWatch({ control, name: ["email", "type"] });
  const otherReps = (repList?.reps ?? []).filter(({ id }) => id !== rep?.id);
  const isDuplicate = hasActiveRep(otherReps, email.trim(), type);
  const saveRep = useSaveCarrierRepMutation(carrier.id, rep?.id);
  const deactivateRep = useDeactivateCarrierRepMutation(carrier.id);
  const [isConfirmingDeactivation, setIsConfirmingDeactivation] =
    useState(false);

  const confirmDeactivation = (deactivatedRep: CarrierRep): void =>
    deactivateRep.mutate(deactivatedRep.id, {
      onSuccess: onClose,
      onSettled: () => setIsConfirmingDeactivation(false),
    });

  const clearSaveErrors = (): void => {
    clearErrors();
    if (saveRep.isError) {
      saveRep.reset();
    }
  };

  const submitRep = (values: CarrierRepFormValues): void =>
    saveRep.mutate(toCarrierRepRequest(values), { onSuccess: onClose });

  return (
    <Dialog
      open
      onClose={onClose}
      title={`${rep ? "Edit" : "Add"} Rep · ${carrier.name}`}
      testIdSuffix="carrier-rep"
      maxWidth={false}
      slotProps={{ paper: { sx: { width: DIALOG_WIDTH } } }}
      actions={
        <>
          {rep && (
            <Button
              variant="secondary"
              sx={{ mr: "auto" }}
              disabled={saveRep.isPending}
              onClick={() => setIsConfirmingDeactivation(true)}
            >
              Deactivate
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit(submitRep)}
            disabled={isDuplicate || deactivateRep.isPending}
            loading={saveRep.isPending}
          >
            Save
          </Button>
        </>
      }
    >
      <Box display="flex" flexDirection="column" gap={2}>
        {saveRep.error && (
          <Alert severity="error">
            {extractErrorMessage(
              saveRep.error,
              rep ? UPDATE_REP_ERROR_MESSAGE : ADD_REP_ERROR_MESSAGE,
            )}
          </Alert>
        )}
        {deactivateRep.error && (
          <Alert severity="error">
            {extractErrorMessage(
              deactivateRep.error,
              DEACTIVATE_REP_ERROR_MESSAGE,
            )}
          </Alert>
        )}
        <SectionTitle title="Rep">
          {rep && <RepStatusChip active={rep.active} />}
        </SectionTitle>
        <Controller
          name="email"
          control={control}
          render={({ field, fieldState }) => (
            <FormField label="Email" htmlFor="carrier-rep-email">
              <TextField
                {...field}
                id="carrier-rep-email"
                onChange={(event) => {
                  clearSaveErrors();
                  field.onChange(event);
                }}
                fullWidth
                placeholder="jane.doe@carrier.com"
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
              />
            </FormField>
          )}
        />
        <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <FormField label="Name" htmlFor="carrier-rep-name">
                <TextField {...field} id="carrier-rep-name" fullWidth />
              </FormField>
            )}
          />
          <Controller
            name="title"
            control={control}
            render={({ field }) => (
              <FormField label="Title" htmlFor="carrier-rep-title">
                <TextField
                  {...field}
                  id="carrier-rep-title"
                  fullWidth
                  placeholder="Account Executive..."
                />
              </FormField>
            )}
          />
          <Controller
            name="phone"
            control={control}
            render={({ field }) => (
              <FormField label="Phone" htmlFor="carrier-rep-phone">
                <TextField {...field} id="carrier-rep-phone" fullWidth />
              </FormField>
            )}
          />
          <Controller
            name="type"
            control={control}
            render={({ field }) => (
              <FormField
                label="Role"
                htmlFor="carrier-rep-role"
                labelId="carrier-rep-role-label"
              >
                <Select
                  {...field}
                  id="carrier-rep-role"
                  labelId="carrier-rep-role-label"
                  onChange={(event) => {
                    clearSaveErrors();
                    field.onChange(event);
                  }}
                  fullWidth
                >
                  {REP_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>
                      {REP_TYPE_LABELS[type]}
                    </MenuItem>
                  ))}
                </Select>
              </FormField>
            )}
          />
        </Box>
        <Controller
          name="notes"
          control={control}
          render={({ field }) => (
            <FormField label="Notes about this rep" htmlFor="carrier-rep-notes">
              <TextField {...field} id="carrier-rep-notes" fullWidth />
            </FormField>
          )}
        />

        <Divider />

        <SectionTitle
          title="Rules"
          description="One block per broker. Everything inside a block applies to this rep with that broker only."
        />
        <Alert severity="warning">
          No Brokers Yet: Without rules, this Rep receives no RFPs. Add one or
          more brokers below.
        </Alert>
        <FormField label="Add brokers" htmlFor="carrier-rep-broker-search">
          <BrokerChecklist
            brokers={MOCK_BROKERS}
            searchInputId="carrier-rep-broker-search"
          />
        </FormField>

        <Divider />

        <SectionTitle title="Checks" />
        <DuplicateCheck isDuplicate={isDuplicate} />
      </Box>
      {rep && (
        <ConfirmationModal
          open={isConfirmingDeactivation}
          onCancel={() => setIsConfirmingDeactivation(false)}
          onAccept={() => confirmDeactivation(rep)}
          loading={deactivateRep.isPending}
          title="Deactivate Rep"
          description={`${rep.email} will be deactivated for ${carrier.name} and can no longer be edited.`}
          confirmButtonText="Deactivate"
          cancelButtonText="Cancel"
        />
      )}
    </Dialog>
  );
};
